import { useState } from "react";
import { listThemeTokens, resolveValue, isTokenRef, type Theme } from "@broadpaper/core";
import { Popover } from "../../ui/floating.js";
import { TextInput, Button } from "../../ui/primitives.js";

const PRESETS = ["#111827", "#374151", "#6B7280", "#9CA3AF", "#D1D5DB", "#F3F4F6", "#FFFFFF", "#B91C1C", "#DC2626", "#EA580C", "#D97706", "#CA8A04", "#65A30D", "#16A34A", "#059669", "#0D9488", "#0891B2", "#0284C7", "#2563EB", "#4F46E5", "#7C3AED", "#9333EA", "#C026D3", "#DB2777"];

export function ColorPicker({ value, onChange, theme, allowTransparent = true }: { value: string; onChange: (v: string) => void; theme: Theme; allowTransparent?: boolean }) {
  const tokens = listThemeTokens(theme).filter((t) => t.group === "colors" || t.group === "table");
  const [hex, setHex] = useState(isTokenRef(value) ? "" : value);
  return (
    <div>
      <div className="bpe-menu__label">Theme colours</div>
      {tokens.map((t) => (
        <button key={t.token} type="button" className="bpe-color-token" aria-pressed={value === t.token} style={{ ["--c" as string]: t.value }} onClick={() => onChange(t.token)}>
          <span className="bpe-color-token__sw" />
          <span className="bpe-truncate">{t.label}</span>
          <span className="bpe-color-token__val">{t.value.toUpperCase()}</span>
        </button>
      ))}
      <div className="bpe-menu__label">Custom</div>
      <div className="bpe-color-grid">
        {PRESETS.map((c) => (
          <button key={c} type="button" className="bpe-color-grid__item" aria-pressed={value.toUpperCase() === c} aria-label={c} style={{ ["--c" as string]: c }} onClick={() => onChange(c)} />
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, padding: 4, alignItems: "center" }}>
        <input type="color" aria-label="Pick colour" value={/^#[0-9a-f]{6}$/i.test(hex) ? hex : resolveValue(value, theme, "#000000")} onChange={(e) => { setHex(e.target.value); onChange(e.target.value); }} style={{ width: 28, height: 28, padding: 0, border: "1px solid var(--bpe-border)", borderRadius: 6, background: "none" }} />
        <TextInput value={hex} onChange={setHex} placeholder="#RRGGBB" mono lazy onBlur={() => /^#[0-9a-f]{6}$/i.test(hex) && onChange(hex)} onKeyDown={(e) => e.key === "Enter" && /^#[0-9a-f]{6}$/i.test(hex) && onChange(hex)} />
        {allowTransparent ? <Button size="sm" onClick={() => onChange("")}>None</Button> : null}
      </div>
    </div>
  );
}

export function ColorField({ value, onChange, theme, allowTransparent, placeholder = "Default" }: { value: string | undefined; onChange: (v: string | undefined) => void; theme: Theme; allowTransparent?: boolean; placeholder?: string }) {
  const v = value ?? "";
  const resolved = v ? resolveValue(v, theme, "transparent") : "transparent";
  const label = !v ? placeholder : isTokenRef(v) ? listThemeTokens(theme).find((t) => t.token === v)?.label ?? v : v.toUpperCase();
  return (
    <Popover className="bpe-color-popover" trigger={<button type="button" className="bpe-color-btn" aria-label={`Colour: ${label}`}><span className="bpe-color-btn__swatch" style={{ ["--c" as string]: resolved }} /><span className="bpe-color-btn__label">{label}</span></button>}>
      {(close) => (
        <ColorPicker
          value={v}
          theme={theme}
          allowTransparent={allowTransparent}
          onChange={(c) => {
            onChange(c || undefined);
            if (!c || isTokenRef(c) || /^#[0-9a-f]{6}$/i.test(c)) close();
          }}
        />
      )}
    </Popover>
  );
}
